import { useState, useEffect } from "react";
import { Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [showMobileCTA, setShowMobileCTA] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
      setShowMobileCTA(window.scrollY > window.innerHeight);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navItems = [
    { label: "Início", href: "#inicio" },
    { label: "Sobre", href: "#programa" },
    { label: "Benefícios", href: "#beneficios" },
    { label: "Como Funciona", href: "#como-funciona" },
    { label: "Bônus", href: "#bonus" },
    { label: "Depoimentos", href: "#depoimentos" },
    { label: "Acesso", href: "#acesso" },
  ];

  const scrollToSection = (href: string) => {
    if (window.location.pathname !== "/") {
      window.location.href = "/" + href;
      return;
    }
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setIsMobileMenuOpen(false);
    }
  };

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled 
          ? "bg-background/95 backdrop-blur-glass shadow-depth border-b border-border-gold/20" 
          : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo / Mobile CTA Button */}
          <div className="flex items-center">
            {/* Desktop Logo - Always visible */}
            <a 
              href="#inicio" 
              onClick={(e) => { e.preventDefault(); scrollToSection("#inicio"); }} 
              className="hidden md:flex items-center group"
            >
              <span className="text-2xl md:text-4xl font-bold tracking-tight">
                <span className="text-primary group-hover:animate-shimmer bg-[length:200%_auto]">
                  Auto
                </span>
                <span className="text-foreground group-hover:text-primary transition-colors">
                  Make
                </span>
              </span>
            </a>

            {/* Mobile Logo - Always visible */}
            <a 
              href="#inicio" 
              onClick={(e) => { e.preventDefault(); scrollToSection("#inicio"); }} 
              className="md:hidden flex items-center group"
            >
              <span className="text-xl font-bold tracking-tight">
                <span className="text-primary">
                  Auto
                </span>
                <span className="text-foreground">
                  Make
                </span>
              </span>
            </a>
          </div>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navItems.map((item) => (
              <a
                key={item.href}
                href={item.href}
                onClick={(e) => {
                  e.preventDefault();
                  scrollToSection(item.href);
                }}
                className="relative px-4 py-2 text-sm font-medium text-foreground/80 hover:text-primary hover:bg-gradient-rose hover:bg-clip-text transition-all duration-300 group"
              >
                {item.label}
                <span className="absolute bottom-0 left-0 w-full h-0.5 bg-gradient-rose scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" />
              </a>
            ))}
          </nav>

          {/* CTA Button Desktop */}
          <div className="hidden lg:block">
            <Button 
              variant="premium"
              size="default"
              onClick={() => scrollToSection("#acesso")}
            >
              Garantir Acesso
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 text-foreground hover:text-primary transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={28} /> : <Menu size={28} />}
          </button>
        </div>

        {/* Mobile Navigation */}
        {isMobileMenuOpen && (
          <nav className="lg:hidden py-8 border-t border-border-gold/20 bg-card-glass/80 backdrop-blur-glass rounded-b-2xl animate-scale-in">
            <div className="flex flex-col space-y-2">
              {navItems.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={(e) => {
                    e.preventDefault();
                    scrollToSection(item.href);
                  }}
                  className="px-4 py-3 text-base font-medium text-foreground/80 hover:bg-gradient-rose hover:bg-clip-text hover:text-transparent hover:bg-primary/10 rounded-lg transition-all"
                >
                  {item.label}
                </a>
              ))}
              <div className="pt-4 px-4">
                <Button 
                  variant="premium"
                  onClick={() => scrollToSection("#acesso")}
                  className="w-full"
                >
                  Garantir Acesso
                </Button>
              </div>
            </div>
          </nav>
        )}
      </div>
    </header>
  );
};

export default Header;
